import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Smartphone, Speaker, Video, ChevronDown, Plus } from "lucide-react";
import { getPackageConfig, formatPrice } from "@/lib/package-config";
import { FeatureTooltip } from "@/components/feature-tooltip";
import { usePricingVisibility } from "@/lib/pricing-visibility";
import type { StickyPricingBarProps } from "./types";

export function StickyPricingBar({
  show,
  selectedCount,
  contextName,
  pricing,
  expandedPackage,
  onTogglePackage,
  onAddPackage,
}: StickyPricingBarProps) {
  const { isPricingVisible } = usePricingVisibility();

  // Hide the sticky bar completely when pricing is hidden
  if (!show || !isPricingVisible) return null;

  // Determine show count based on pricing structure
  const setCoreShowCount = pricing.setCoreShowCount ?? pricing.showCount ?? 1;
  const setAudioShowCount = pricing.setAudioShowCount ?? pricing.showCount ?? 1;
  const setVideoShowCount = pricing.setVideoShowCount ?? pricing.showCount ?? 1;

  return (
    <div className="fixed top-16 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b shadow-sm">
      <div className="max-w-6xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge className="bg-gray-100 text-gray-700">
              {selectedCount} show{selectedCount !== 1 ? 's' : ''} selected
            </Badge>
            <span className="text-sm text-gray-600">{contextName}</span>
          </div>

          <div className="flex items-center gap-3">
            {/* SET Core Mini Card */}
            <div className="relative package-dropdown-container">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50 via-slate-100/50 to-slate-200/30">
                <div className="flex flex-col items-center">
                  <Smartphone className="w-5 h-5 text-gray-600" />
                  <button
                    onClick={() => onTogglePackage('set-core')}
                    className="mt-1 p-0.5 hover:bg-gray-100 rounded transition-colors"
                  >
                    <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${expandedPackage === 'set-core' ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                <div className="text-right">
                  {isPricingVisible ? (
                    <>
                      <div className="text-base font-bold text-gray-600">
                        {formatPrice(pricing.setCore)}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {setCoreShowCount > 0 ? formatPrice(Math.round(pricing.setCore / setCoreShowCount)) : '$0'}/show
                      </div>
                    </>
                  ) : (
                    <div className="text-[11px] text-gray-600 font-medium whitespace-nowrap">
                      Contact for<br />Pricing
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onAddPackage('set-core', pricing.setCore)}
                  className="h-6 px-2 text-xs border-gray-300"
                >
                  <Plus className="w-3 h-3" />
                  <span className="text-lg -ml-2">({setCoreShowCount})</span>
                </Button>
              </div>

              {/* SET Core Dropdown */}
              {expandedPackage === 'set-core' && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3">
                  <div className="text-sm font-medium text-gray-900 mb-2">SET Core Package Features</div>
                  <div className="space-y-1">
                    {getPackageConfig('set-core').features.map((feature, index) => (
                      <div key={index} className="flex items-start text-xs text-gray-600">
                        <span className="text-gray-400 mr-2 mt-0.5">•</span>
                        <FeatureTooltip
                          text={feature.text}
                          title={feature.details?.title}
                          description={feature.details?.description}
                          imageUrl={feature.details?.imageUrl}
                          isDynamic={feature.isDynamic}
                          dynamicText={feature.dynamicText}
                          selectedShowCount={setCoreShowCount}
                          className="text-xs"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* SET Audio Mini Card */}
            <div className="relative package-dropdown-container">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 via-blue-100/50 to-blue-200/30">
                <div className="flex flex-col items-center">
                  <Speaker className="w-5 h-5 text-blue-600" />
                  <button
                    onClick={() => onTogglePackage('set-audio')}
                    className="mt-1 p-0.5 hover:bg-blue-100 rounded transition-colors"
                  >
                    <ChevronDown className={`w-3 h-3 text-blue-500 transition-transform ${expandedPackage === 'set-audio' ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                <div className="text-right">
                  {isPricingVisible ? (
                    <>
                      <div className="text-base font-bold text-blue-600">
                        {formatPrice(pricing.setAudio)}
                      </div>
                      <div className="text-[11px] text-blue-500">
                        {setAudioShowCount > 0 ? formatPrice(Math.round(pricing.setAudio / setAudioShowCount)) : '$0'}/show
                      </div>
                    </>
                  ) : (
                    <div className="text-[11px] text-blue-600 font-medium whitespace-nowrap">
                      Contact for<br />Pricing
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => onAddPackage('set-audio', pricing.setAudio)}
                  className="h-6 px-2 text-xs bg-blue-600 hover:bg-blue-700 border-0"
                >
                  <Plus className="w-3 h-3" />
                  <span className="text-lg -ml-2">({setAudioShowCount})</span>
                </Button>
              </div>

              {/* SET Audio Dropdown */}
              {expandedPackage === 'set-audio' && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-blue-200 rounded-lg shadow-lg z-50 p-3">
                  <div className="text-sm font-medium text-blue-900 mb-2">SET Audio Package Features</div>
                  <div className="space-y-1">
                    {getPackageConfig('set-audio').features.map((feature, index) => (
                      <div key={index} className="flex items-start text-xs text-gray-600">
                        <span className={`mr-2 mt-0.5 ${feature.highlight ? 'text-blue-600 font-bold' : 'text-gray-400'}`}>•</span>
                        <FeatureTooltip
                          text={feature.text}
                          title={feature.details?.title}
                          description={feature.details?.description}
                          imageUrl={feature.details?.imageUrl}
                          isDynamic={feature.isDynamic}
                          dynamicText={feature.dynamicText}
                          selectedShowCount={setAudioShowCount}
                          className={`text-xs ${feature.highlight ? 'text-blue-600 font-medium' : ''}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* SET Video Mini Card */}
            <div className="relative package-dropdown-container">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-purple-200 bg-gradient-to-br from-purple-500/15 to-blue-500/15">
                <div className="flex flex-col items-center">
                  <Video className="w-5 h-5 text-purple-600" />
                  <button
                    onClick={() => onTogglePackage('set-video')}
                    className="mt-1 p-0.5 hover:bg-purple-100 rounded transition-colors"
                  >
                    <ChevronDown className={`w-3 h-3 text-purple-500 transition-transform ${expandedPackage === 'set-video' ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                <div className="text-right">
                  {isPricingVisible ? (
                    <>
                      <div className="text-base font-bold text-purple-600">
                        {formatPrice(pricing.setVideo)}
                      </div>
                      <div className="text-[11px] text-purple-500">
                        {setVideoShowCount > 0 ? formatPrice(Math.round(pricing.setVideo / setVideoShowCount)) : '$0'}/show
                      </div>
                    </>
                  ) : (
                    <div className="text-[11px] text-purple-600 font-medium whitespace-nowrap">
                      Contact for<br />Pricing
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => onAddPackage('set-video', pricing.setVideo)}
                  className="h-6 px-2 text-xs bg-purple-600 hover:bg-purple-700 border-0"
                >
                  <Plus className="w-3 h-3" />
                  <span className="text-lg -ml-2">({setVideoShowCount})</span>
                </Button>
              </div>

              {/* SET Video Dropdown */}
              {expandedPackage === 'set-video' && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-purple-200 rounded-lg shadow-lg z-50 p-3">
                  <div className="text-sm font-medium text-purple-900 mb-2">SET Video Package Features</div>
                  <div className="space-y-1">
                    {getPackageConfig('set-video').features.map((feature, index) => (
                      <div key={index} className="flex items-start text-xs text-gray-600">
                        <span className={`mr-2 mt-0.5 ${feature.highlight ? 'text-purple-600 font-bold' : 'text-gray-400'}`}>•</span>
                        <FeatureTooltip
                          text={feature.text}
                          title={feature.details?.title}
                          description={feature.details?.description}
                          imageUrl={feature.details?.imageUrl}
                          isDynamic={feature.isDynamic}
                          dynamicText={feature.dynamicText}
                          selectedShowCount={setVideoShowCount}
                          className={`text-xs ${feature.highlight ? 'text-purple-600 font-medium' : ''}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
